import React, { memo, useCallback, useMemo } from 'react';
import {
	Control,
	FieldErrors,
	FieldValues,
	UseFormRegister,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';

import { Button, Datepicker, FoldPanel, Label } from '@components/common';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IModalSelectOption } from '@components/common/ModalSelect/ModalSelect.types';

import { EParametersFormValues } from '../enums';

import ConsistencyCheckDefaultOptions from './ConsistencyCheckDefaultOptions/ConsistencyCheckDefaultOptions';
import GoldenSettingsLabelInfo from '../GoldenSettingsForm/GoldenSettingsLabelInfo';
import ConsistencyCheckMavenirOptions from './ConsistencyCheckMavenirOptions';

import styles from './ConsistencyCheckOptions.module.scss';

interface IConsistencyOptionsProps {
	control: Control;
	datesOptions: Array<Date>;
	errors: FieldErrors;
	goldenSettingsDate?: string;
	handleSubmit: () => void;
	isDisabledSubmit: boolean;
	isLoading: boolean;
	isOpenConsistencyOptions: boolean;
	importFileName?: string;
	nodeTypesOptions: Array<TSelectOption>;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	showGoldenSettings: () => void;
	technologiesOptions: Array<TSelectOption>;
	nodeOptions: IModalSelectOption[];
	configTypeOptions: Array<TSelectOption>;
	versionOptions: Array<TSelectOption>;
	toggleConsistencyOptions: () => void;
	watch: UseFormWatch<FieldValues>;
	isMavenirVendor: boolean;
}

const ConsistencyOptions = ({
	control,
	datesOptions,
	errors,
	goldenSettingsDate,
	handleSubmit,
	isDisabledSubmit,
	isLoading,
	isOpenConsistencyOptions,
	importFileName,
	nodeTypesOptions,
	setValue,
	showGoldenSettings,
	technologiesOptions,
	configTypeOptions,
	nodeOptions,
	versionOptions,
	toggleConsistencyOptions,
	isMavenirVendor,
	watch,
}: IConsistencyOptionsProps) => {
	const nodeType = watch(EParametersFormValues.nodeType);
	const vendor = watch(EParametersFormValues.vendor);
	const technology = watch(EParametersFormValues.technology);
	const nodes = watch(EParametersFormValues.nodes);
	const version = watch(EParametersFormValues.version);
	const configType = watch(EParametersFormValues.configType);
	const date = watch(EParametersFormValues.configurationDate);

	const areOptionsNotSelected = useMemo(() => {
		return isMavenirVendor
			? !(vendor && nodes && version && configType)
			: !(vendor && technology && nodeType);
	}, [configType, isMavenirVendor, nodeType, nodes, technology, vendor, version]);

	const setDataSource = useCallback((date?: number | null) => {
		setValue(EParametersFormValues.configurationDate, date);
	}, [setValue]);

	return (
		<FoldPanel
			buttonsComponent={
				<div className={styles.consistencyCheckOptionsButton}>
					<Button
						disabled={isDisabledSubmit}
						large
						onClick={handleSubmit}
						text='Apply MO Branches'
					/>
				</div>
			}
			isLoading={isLoading}
			isOpen={isOpenConsistencyOptions}
			title='Consistency Check Options'
			togglePanel={toggleConsistencyOptions}
			width='400px'
		>
			{
				isMavenirVendor
					? (
						<ConsistencyCheckMavenirOptions
							configTypesOptions={configTypeOptions}
							control={control}
							errors={errors}
							nodeIdsOptions={nodeOptions}
							nodes={nodes}
							setValue={setValue}
							vendor={vendor}
							version={version}
							versionsOptions={versionOptions}
						/>
					)
					: (
						<ConsistencyCheckDefaultOptions
							control={control}
							errors={errors}
							nodeTypesOptions={nodeTypesOptions}
							technologiesOptions={technologiesOptions}
							watch={watch}
						/>
					)
			}
			<div className={styles.datepickerWrapper}>
				<Datepicker
					disableInput
					disabled={areOptionsNotSelected}
					handleChange={setDataSource}
					includeDates={datesOptions}
					isMobileDevice={false}
					isTodayButton={false}
					label='Consistency Check dated'
					required
					selected={date ? new Date(date) : null}
				/>
			</div>
			<div className={styles.goldenSettingsButton}>
				<Label
					className={styles.goldenSettingsLabel}
					label='Golden Settings'
					required
				/>
				<Button
					disabled={areOptionsNotSelected}
					large
					onClick={showGoldenSettings}
					secondary
					text='Manage Golden Settings'
				/>
				{!areOptionsNotSelected &&
					<GoldenSettingsLabelInfo
						date={goldenSettingsDate}
						emptyText={`Golden settings are not set. Please use 'Manage Golden settings'`}
						importFileName={importFileName}
					/>
				}
			</div>
		</FoldPanel>
	);
};

export default memo(ConsistencyOptions);
